import { useMemo, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import Skeleton from 'react-loading-skeleton'
import { useLocation } from 'react-router-dom'
import { Loading } from '@winnin/winnin_ui.components.loading'
import { Title } from '@winnin/winnin_ui.components.title'

import LoadingErrorWrapper from 'components/LoadingErrorWrapper'
import SkeletonTable from 'components/SkeletonTable'
import { TerritoriesTable } from 'components/TerritoriesTable'
import TerritoryChipsFilter from 'components/TerritoryChipsFilter'
import getTerritoryFiltersFromUrl from 'components/TerritoryChipsFilter/utils/getTerritoryFiltersFromUrl'
import { ListTerrotoriesSortFields, ListTerrotoriesSortOrder } from 'generated/graphql'
import { T } from 'lib/tolgee'
import useGetUnifiedTerritories from 'modules/territory/common/hooks/use-get-unified-territories'
import { getVariables } from 'pages/SearchTerritoriesPage/utils/getVariables'
import { analyticsTracker, TOLGEE_GLOBALS } from 'utils'

import './index.scss'

interface IFiltersView {
  searchFilter: string
}

const { SORT } = analyticsTracker.eventNames

export default function FiltersView({ searchFilter }: IFiltersView) {
  const [order, setOrder] = useState<{ field: ListTerrotoriesSortFields; order: ListTerrotoriesSortOrder }>({
    field: ListTerrotoriesSortFields.AvgEngagement,
    order: ListTerrotoriesSortOrder.Desc,
  })
  const location = useLocation()

  const filters = useMemo(() => {
    return getTerritoryFiltersFromUrl(location.search)
  }, [location.search])

  const variables = useMemo(() => {
    return getVariables(filters, searchFilter, order)
  }, [filters, order, searchFilter])

  const { data, error, fetchMore, isLoading, refetch, hasMore, totalCount, isFetchingMore } = useGetUnifiedTerritories({
    variables,
    audience: filters?.audience,
  })

  const [sentryRef] = useInfiniteScroll({
    loading: isLoading || isFetchingMore,
    hasNextPage: hasMore,
    onLoadMore: fetchMore,
    disabled: !!error,
  })

  const handleSetOrder = field => {
    setOrder(prevState => {
      const previousOrder = prevState.order === 'desc' ? ListTerrotoriesSortOrder.Asc : ListTerrotoriesSortOrder.Desc
      const newOrder = {
        field,
        order: prevState.field === field ? previousOrder : ListTerrotoriesSortOrder.Desc,
      }

      analyticsTracker.eventTrack(SORT, {
        Page: 'Search Topics Page',
        'Sort field': field,
        'Sort order': newOrder.order,
      })

      return newOrder
    })
  }

  return (
    <div className="FiltersView-wrapper">
      <div className="countSection">
        {isLoading ? (
          <Skeleton
            height={12}
            borderRadius={20}
            width={150}
            containerClassName="SkeletonTable-lines"
            baseColor="var(--color_neutral_400)"
          />
        ) : (
          <Title tag="h3">
            <T
              keyName={TOLGEE_GLOBALS.results.key}
              parameters={{
                results: totalCount ?? 0,
              }}
            >
              {`${TOLGEE_GLOBALS.results.defaultValue}`}
            </T>
          </Title>
        )}
      </div>
      <div className="filtersSection">
        <TerritoryChipsFilter />
      </div>
      <div className="tableSection">
        <LoadingErrorWrapper
          error={error}
          loading={isLoading}
          refetch={refetch}
          isEmpty={!data?.length}
          skeletonComponent={SkeletonTable}
        >
          <>
            <TerritoriesTable
              territories={data}
              handleSort={handleSetOrder}
              sortOrder={order.order}
              sortField={order.field}
              allowSortByVideos
            />
            {(isFetchingMore || hasMore) && (
              <div ref={sentryRef}>
                <Loading />
              </div>
            )}
          </>
        </LoadingErrorWrapper>
      </div>
    </div>
  )
}
